import { useCallback, useEffect, useRef, useState } from 'react';

import { useSearchParams } from 'react-router-dom';

import { ExportProgressView } from '@/components/export-progress-view';
import { useAutoDownloadPreference } from '@/hooks/use-auto-download-preference';
import { useBulkPdfExport } from '@/hooks/use-bulk-pdf-export';
import { useCsvExport } from '@/hooks/use-csv-export';
import { useRegisterPdfExport } from '@/hooks/use-register-pdf-export';
import { useSingleRecordPdfExport } from '@/hooks/use-single-record-pdf-export';
import { EXPORT_JOB_PARAM, readExportJob, type ExportJob } from '@/utils/export-tab';

type ExportStatus = 'idle' | 'generating' | 'ready' | 'error';

function titleForStatus(status: ExportStatus, progress: number): string {
  switch (status) {
    case 'generating':
      return `Downloading… ${progress}% — Gearbelt`;
    case 'ready':
      return 'Download ready — Gearbelt';
    case 'error':
      return 'Export failed — Gearbelt';
    default:
      return 'Preparing export — Gearbelt';
  }
}

type ExportRunnerViewProps = {
  label: string;
  status: ExportStatus;
  progress: number;
  downloadUrl: string | null;
  start: () => void;
  reset: () => void;
};

function ExportRunnerView({ label, status, progress, downloadUrl, start, reset }: ExportRunnerViewProps) {
  const [autoDownload, setAutoDownload] = useAutoDownloadPreference();
  const autoOpenedRef = useRef(false);

  // `start` is stable and `generate` aborts any prior run, so re-running this on
  // a StrictMode remount restarts cleanly rather than stranding the progress bar.
  useEffect(() => {
    start();
  }, [start]);

  useEffect(() => {
    if (status === 'ready' && downloadUrl && autoDownload && !autoOpenedRef.current) {
      autoOpenedRef.current = true;
      window.open(downloadUrl, '_blank');
    }
    if (status !== 'ready') autoOpenedRef.current = false;
  }, [status, downloadUrl, autoDownload]);

  useEffect(() => {
    document.title = titleForStatus(status, progress);
  }, [status, progress]);

  return (
    <ExportProgressView
      // Before the mount effect fires, the hook is still `idle`; render it as the generating screen.
      status={status === 'idle' ? 'generating' : status}
      progress={progress}
      label={label}
      downloadUrl={downloadUrl}
      autoDownload={autoDownload}
      onAutoDownloadChange={setAutoDownload}
      onDownload={() => {
        if (downloadUrl) window.open(downloadUrl, '_blank');
      }}
      onRetry={() => {
        autoOpenedRef.current = false;
        reset();
        start();
      }}
    />
  );
}

function CsvExportRunner({ request }: { request: Extract<ExportJob, { kind: 'csv' }>['request'] }) {
  const { status, progress, downloadUrl, generate, reset } = useCsvExport();
  const start = useCallback(() => void generate(request), [generate, request]);
  return <ExportRunnerView label="CSV" status={status} progress={progress} downloadUrl={downloadUrl} start={start} reset={reset} />;
}

function PdfExportRunner({ request }: { request: Extract<ExportJob, { kind: 'pdf' }>['request'] }) {
  const { status, progress, downloadUrl, generate, reset } = useSingleRecordPdfExport();
  const start = useCallback(() => void generate(request), [generate, request]);
  return <ExportRunnerView label="PDF" status={status} progress={progress} downloadUrl={downloadUrl} start={start} reset={reset} />;
}

function BulkPdfExportRunner({ request }: { request: Extract<ExportJob, { kind: 'pdf-bulk' }>['request'] }) {
  const { status, progress, downloadUrl, generate, reset } = useBulkPdfExport();
  const start = useCallback(() => void generate(request), [generate, request]);
  return <ExportRunnerView label="PDF" status={status} progress={progress} downloadUrl={downloadUrl} start={start} reset={reset} />;
}

function RegisterPdfExportRunner({ request }: { request: Extract<ExportJob, { kind: 'pdf-scoped' }>['request'] }) {
  const { status, progress, downloadUrl, generate, reset } = useRegisterPdfExport();
  const start = useCallback(() => void generate(request), [generate, request]);
  return <ExportRunnerView label="PDF" status={status} progress={progress} downloadUrl={downloadUrl} start={start} reset={reset} />;
}

function InvalidExportView() {
  return (
    <div className="bg-grey-1 flex min-h-[100dvh] items-center justify-center p-6">
      <div className="flex w-full max-w-md flex-col items-center gap-[10px] rounded-lg bg-white p-8 text-center shadow-sm">
        <h1 className="text-default-text text-lg font-medium">This export link is no longer available</h1>
        <p className="text-default-text text-sm">The download may have expired. Please close this tab and start the export again from Gearbelt.</p>
      </div>
    </div>
  );
}

export function ExportProgressPage() {
  const [searchParams] = useSearchParams();
  const [job] = useState<ExportJob | null>(() => readExportJob(searchParams.get(EXPORT_JOB_PARAM)));

  if (!job) return <InvalidExportView />;
  switch (job.kind) {
    case 'csv':
      return <CsvExportRunner request={job.request} />;
    case 'pdf':
      return <PdfExportRunner request={job.request} />;
    case 'pdf-bulk':
      return <BulkPdfExportRunner request={job.request} />;
    case 'pdf-scoped':
      // Both 'register' and 'register-and-records' POST to the same pdf-register
      // endpoint and poll the same run, so they share the one runner.
      return <RegisterPdfExportRunner request={job.request} />;
  }
}

export default ExportProgressPage;
